import {
	LoadPoolProps,
	GetProductsResp,
	TProductElement as TFProductElement,
	TProductModule,
	PoolExtraInfo,
	TProductBaseProps,
} from "views/services/product";
import { ModuleLayoutStyle, TTagModule, International } from "views/services/playground";
import { WebProductsProps } from "views/pc/components/NormalProducts";
import {
	TProductModule as ServerProductModule,
	Ui,
	Shape,
	TProductElement,
	TMultiProductModule,
	TModuleHeader,
	TPlatform,
	Layout,
} from "services/page/admin";
import { ExtraInfo, FrontMeta } from "services/pool/app";
import { GetProducts } from "services/page/app";
import { TCombinedProductCommonExtend } from "services/page/modules";

import { genPrmLink } from "utils/url";
import { isNotBlankArray } from "utils/array";
import { PrmParams } from "../prm";
import { transformProductPrice } from "utils/price";
import { ProductShape } from "views/mobile/constants";
import { GetAutoLink } from "utils/browser/app";
import { CommonRuntime, getMeta } from "utils/runtime";
import { SetLitbCtrTrackOnAction, SetLitbCtrTrackOnShow } from "utils/ctrTrack";

import {
	replaceUrlWithTag,
	heightLightTabIndex,
	ParseProductTagFromUrlResp,
} from "utils/playground/url";
import { getPriceDisplay, getSymbolByCurreny } from "utils/currency";
import { px2rem } from "utils/browser/resizer";
import { getGetProductsParams } from "utils/zeusFetch";

// 商品图片占位图
export const placeholderGroup = (shape: Shape) => {
	if (shape === Shape.Square) {
		return "https://i.ezbuy.sg/Fgu47zjp9ABUt-ZQMKa7J-7M-aoW";
	}
	return "https://i.ezbuy.sg/FinkFHyvFkDe4HOIJ2zEhe9-YgVM";
};

export const transformProductModule = (
	items: ServerProductModule[],
	extend: TCombinedProductCommonExtend,
	runtime: CommonRuntime,
	prm: PrmParams,
): {
	tagGroup: TTagModule[];
	activeTab: string;
	nextItems: TProductModule[];
} => {
	if (!isNotBlankArray(items)) {
		return null;
	}
	const { ui, maxLoad, canLoadMore } = extend;
	const nextItems = [...items].map((item) => {
		const { productElements } = item;
		if (productElements) {
			return {
				...item,
				maxLoad: item.maxLoad || maxLoad,
				productElements: productElements.map((pElement, pIndex) => {
					return transformProduct(pElement, ui, runtime, { ...prm, position: pIndex });
				}),
			};
		}
		return {
			...item,
			canLoadMore,
			maxLoad: item.maxLoad || maxLoad,
		};
	}) as TProductModule[];

	const { tagGroup, activeTab } = transformProductTag(items, extend, runtime);
	return {
		tagGroup,
		activeTab,
		nextItems,
	};
};

function transformProduct(
	pElement: TProductElement,
	ui: Ui,
	runtime: CommonRuntime,
	prm: PrmParams,
) {
	const { floor, position } = prm;
	const nextLink = genPrmLink(
		{
			floor,
			position: position + 1,
		},
		runtime,
		transformProductDetailUrl(pElement.url),
	);
	const imgFromShape = pElement.extra.img;
	// 兼容后台管理配置值的历史错误值（后台选择 square，上报的value = rectA， rectA=ReacB）
	let img = [Shape.Square].includes(ui?.shape) ? imgFromShape.square : imgFromShape.rectangleA;
	const extra = transformProductExtra(pElement);
	img = img || pElement.img;

	const priceGraph = getSymbolByCurreny(runtime?.currency);

	return {
		...pElement,
		url: GetAutoLink(nextLink),
		img,
		price: transformProductPrice(pElement.price, { ...runtime, priceGraph }),
		originPrice: transformProductPrice(pElement.originPrice, { ...runtime, priceGraph }, true),
		extra,
	};
}

function transformProductExtra(product: TProductElement): PoolExtraInfo {
	const { extraProductInfo, rightView, leftView, flash } = product.extra || ({} as ExtraInfo);

	return {
		imgOnTopRight: { img: extraProductInfo?.imgOnTopRight?.imgs?.[0] || "", height: "16px" },
		imgOnBottomLeft: { img: extraProductInfo?.imgOnBottomLeft?.imgs?.[0] || "" },
		imgOnTopLeft: { img: extraProductInfo?.imgOnTopLeft?.imgs[0] || "", height: "16px" },
		actTags: extraProductInfo?.titleTag?.imgs,
		collect: {
			text: rightView?.text,
			score: leftView?.rateScore || leftView?.text,
		},
		flash: {
			discount: flash?.discount,
			countdownToEnd: flash?.countdownToEnd,
			uiOptions: {
				icon: "iconfont icon-flashsaleicon",
			},
		},
		shipIn24hours: extraProductInfo?.extraTag?.text,
	};
}
function transformProductTag(
	items: ServerProductModule[],
	extend: TCombinedProductCommonExtend,
	runtime: CommonRuntime,
) {
	let activeTab = "";
	const { ui } = extend;
	let { tabExpand } = extend;
	const result = items.reduce((res, item, index) => {
		const { name, poolId, productElements, ig } = item;
		if (item.tabExpand) {
			tabExpand = item.tabExpand;
		}

		if (!activeTab && productElements) {
			activeTab = `${poolId}`;
		}
		const textFocusColor = getColorFromTable(ui, "textFocusColor", "#fff");
		const bgColor = ui?.bgColor || "#fff";
		const tagFocusBg = getColorFromTable(ui, "tagFocusBg", "#ff5a00");
		const textColor = getColorFromTable(ui, "texColor", "#333");
		const igGroup: International = ig?.igroup?.[runtime.languageCode];
		const igName = igGroup?.text;
		return [
			...res,
			{
				id: poolId,
				positionId: `${poolId}|${index}`,
				tabExpand,
				bgColor,
				focusBgColor: tagFocusBg,
				text: {
					content: igName || name,
					color: textColor,
					lightColor: textFocusColor,
				},
			},
		];
	}, []);
	return {
		activeTab,
		tagGroup: result.length > 1 ? result : [],
	};
}

const getColorFromTable = (source: Ui, key: string, defaultColor: string = "") => {
	return source?.colorTable?.[key] ? source.colorTable[key] : defaultColor;
};

/**
 * 商品card poster的高宽比
 */
export const fetchProductPosterRadio = (shape: Shape): number => {
	if (shape === Shape.RectangleB) {
		return 1.25;
	}
	return 1;
};
/**
 * front 对加载商品的回调事件
 */
export const loadPoolByFront =
	({ ui, prm, runtime }: { ui: Ui; prm: PrmParams; runtime: CommonRuntime }) =>
	async (props: LoadPoolProps): Promise<GetProductsResp> => {
		const { poolId, limit, offset, prmPosition } = props;
		const metaData: FrontMeta = getMeta(runtime);

		const pool = await GetProducts(
			{
				poolId,
				offset: offset || "0",
				limit,
				meta: {
					...metaData,
					userInfos: {
						...getGetProductsParams(runtime),
					},
				},
			},
			{ runtime },
		).catch((error) => {
			console.error(error);
		});
		if (pool) {
			const nextProducts = pool.products.map((pElement: TProductElement, pIndex: number) => {
				return transformProduct(pElement, ui, runtime, {
					...prm,
					position: pIndex + prmPosition,
				});
			}) as TFProductElement[];

			/* 查看front 商品池请求信息 */
			return {
				hasNext: !!pool.hasNext,
				products: nextProducts,
			};
		}
		return {
			hasNext: false,
			products: [],
		};
	};

/**
 * M productcard style
 */
interface ProductCardStyleProps {
	shape?: Shape;
	col: number;
}
export const fetchMProductCardStyle = (props: ProductCardStyleProps) => {
	if (props.col === 1) {
		return {
			height: px2rem(120),
			width: px2rem(120),
		};
	}
	if (props.shape) {
		return ProductShape[props.shape];
	}
	return {};
};

interface HandleProductModuleParamProps {
	defaultVal: Partial<WebProductsProps>;
	module: TMultiProductModule;
	runtime: CommonRuntime;
	prm: PrmParams;
	header: TModuleHeader;
	extend: TCombinedProductCommonExtend;
	tagQuery: ParseProductTagFromUrlResp;
	isRollable?: boolean;
}
export const handleProductModuleParam = ({
	defaultVal,
	module,
	prm,
	runtime,
	header,
	tagQuery,
	extend,
	isRollable,
}: HandleProductModuleParamProps): TProductBaseProps => {
	const { ui } = extend;
	const { tagGroup, nextItems } =
		transformProductModule(module.proModules, extend, runtime, prm) || {};
	let activeTabIndex = 0;
	if (header?.position === tagQuery.modId) {
		activeTabIndex = heightLightTabIndex(nextItems, tagQuery.tagName) || 0;
	}
	return {
		...defaultVal,
		productModules: nextItems,
		tagGroup,
		placeholderBg: placeholderGroup(ui?.shape),
		layoutStyle: ModuleLayoutStyle.Orange,
		canScrollFixed: true,
		notUseQnparams: true,
		activeTab: nextItems?.[activeTabIndex]?.poolId,
		scoreOptions: {
			icon: "iconfont icon-ic_star",
			withStar: !isRollable,
			widthScore: true,
		},
		onLoadPool: loadPoolByFront({
			prm,
			ui,
			runtime,
		}),
		smartImageProps: {
			qnParams: {
				format: "jpg",
			},
		},
		onClickTab: (i: number) => {
			replaceUrlWithTag(header.position, tagGroup[i]?.text?.content);
		},
		SetCtrTrackOnAction: (pos: string, pid: string) =>
			SetLitbCtrTrackOnAction({ cpid: pid, pos }, runtime),
		SetCtrTrackOnShow: (pos: string, pids: string[] = []) => {
			SetLitbCtrTrackOnShow({ pids: pids.join(","), pos }, runtime);
		},
	} as TProductBaseProps;
};
interface SetProductDisplayInfoProps {
	isRollable: boolean;
	platform: TPlatform;
	layout?: Layout;
	currency?: string;
}
export const setProductDisplayInfo = ({
	isRollable,
	platform,
	layout,
	currency,
}: SetProductDisplayInfoProps) => {
	if (platform === TPlatform.Mobile) {
		return {
			hideTitle: !!isRollable,
			hideShipIn24hours: !!isRollable,
			priceHasLineBreak: getPriceDisplay({
				columnNum: layout?.columnNum,
				isRollable,
				currency,
			}),
		};
	}

	return {
		slideWheel: isRollable && {
			width: "30px",
			height: "54px",
		},
	};
};
/** 转换商品url 为/p */
function transformProductDetailUrl(link: string = ""): string {
	if (!link) {
		return "";
	}

	if (/^http(s)?/.test(link)) {
		return link.replace(/^http(s)?:\/\/\w+.lightinthebox.com/, "");
	}
	return link;
}
